import type { Presence, PresenceSummary } from '@cyphex/types/presence';
import { apiGet, apiPatch, apiPost } from '@/lib/api/client';
import { getRealtimeClient } from '@/lib/realtime';
import { emptySnapshot, type PresenceApi } from './presenceAdapter';
import { activityFromWire, fromWire } from './presenceResolver';
import { FROM_WIRE_MANUAL, RESET_AFTER_MINUTES, TO_WIRE_MANUAL } from './presenceWire';
import type { PresenceSnapshot, SelfDeclaration } from './types';

const epoch = (value: string | null): number | null => (value === null ? null : Date.parse(value));

/** `/presence/bulk` answers with a summary per id, not a full presence row. */
function fromSummary(userId: string, summary: PresenceSummary): PresenceSnapshot {
  const base = emptySnapshot(userId, Date.parse(summary.since) || Date.now());
  const state = fromWire(summary.state);
  const status = summary.status ?? null;
  return {
    ...base,
    /*
     * THE RESOLVED STATE, NOT `auto_state` — and the name is the lie, not the
     * value. `PresenceSummary` carries no `auto_state` and no
     * `connection_count`; it carries the ANSWER. For a roster row that is all
     * anybody needs, because a roster row is rendered from `effectiveState`
     * verbatim and never re-resolved. What must not happen is this value being
     * fed back into the resolver as though it were an input, which is why
     * `applyDeltas` no longer adopts it into `self` — see presenceStore.ts.
     */
    autoState: state,
    /*
     * THE MIDDLE INPUT. Both the cold `/presence/bulk` read and every realtime
     * frame land in this one function, and neither carried the activity — so a
     * snapshot for somebody demonstrably on a call arrived with nothing in the
     * field the resolver reads, and the client re-derived `available` from the
     * two inputs it did have. `PresenceSummary.activityState` is required on the
     * wire precisely so a producer cannot leave this a silent null.
     */
    activityState: activityFromWire(summary.activityState),
    effectiveState: state,
    /* THE FIELD EVERY "last seen" SURFACE WAS MISSING. Both the cold REST read
       and the realtime frame land here, and neither was mapped — so the snapshot
       kept `emptySnapshot`'s placeholder and the profile card measured "last
       seen" from its own parse time rather than from the person. */
    lastActivityAt: epoch(summary.lastActivityAt ?? null),
    /* INFERRED, not received: the server resolved them to something other than
       Offline, so somebody is home. Sound for a roster row, and never used for
       one — `effectiveState` above is what renders. It is emphatically NOT the
       signed-in user's own socket count; adopting it into `self` is what pinned
       the owner's own chip to Offline for a whole session. */
    connectionCount: state === 'offline' ? 0 : 1,
    /*
     * THE SERVER HAS ANSWERED — SAY SO.
     *
     * `base` is `emptySnapshot`, whose `resolved` is false because nothing has
     * been heard yet. Every other field is overridden below it, and this one
     * was not, so every roster snapshot stayed "unresolved" for its whole life
     * even though it was built FROM a server payload.
     *
     * That is why "last seen" never appeared anywhere. ProfileCard reads
     * `snapshot?.resolved === true ? snapshot.lastActivityAt : null`, so the
     * timestamp — correctly stored on the row, correctly sent by `toSummary`,
     * correctly parsed one line above — was discarded at the point of use.
     * Same shape as the `hydrateSelf` bug that left the presence menu on
     * "Checking…": the answer had arrived and a flag said otherwise.
     */
    resolved: true,
    status:
      status === null
        ? null
        : {
            emoji: status.emoji,
            text: status.text ?? '',
            expiresAt: status.expiresAt === null ? null : Date.parse(status.expiresAt),
            pinInChat: status.pinInChat,
          },
  };
}

async function fetchManyPresence(userIds: readonly string[]): Promise<PresenceSnapshot[]> {
  if (userIds.length === 0) return [];
  const payload = await apiPost<{ presence: Record<string, PresenceSummary> }>(
    '/presence/bulk',
    { userIds: [...userIds] },
  );
  return userIds.map((id) => {
    const summary = payload.presence[id];
    return summary === undefined ? emptySnapshot(id) : fromSummary(id, summary);
  });
}

/** Two wakes a few milliseconds apart (focus + visibilitychange) are one wake. */
const REFRESH_MIN_GAP_MS = 2_000;

/**
 * Live presence: REST for the cold render, the gateway for every change after
 * that. There is deliberately NO polling — a `presence:<userId>` subscription
 * plus the socket heartbeat is what keeps a roster of 500 avatars accurate for
 * the cost of one connection.
 */
export const presenceHttp: PresenceApi = {
  async fetchSelf(): Promise<SelfDeclaration> {
    const me = await apiGet<Presence>('/presence/me');
    const status = me.status ?? null;
    return {
      userId: me.userId,
      /* Server-owned and this tab cannot see it — a call in the desktop shell's
         other window is invisible here. Hydrating it is what makes a reload
         mid-call come back as In a call instead of Available. */
      activityState: activityFromWire(me.activityState),
      manualState: me.manualState === null ? null : (FROM_WIRE_MANUAL[me.manualState] ?? null),
      manualExpiresAt: epoch(me.manualExpiresAt),
      status:
        status === null || (status.emoji === null && status.text === null)
          ? null
          : {
              emoji: status.emoji,
              text: status.text ?? '',
              expiresAt: epoch(status.expiresAt),
              pinInChat: status.pinInChat,
            },
      ooo: {
        enabled: me.outOfOffice.enabled,
        message: me.outOfOffice.message ?? '',
        startsAt: epoch(me.outOfOffice.startsAt),
        endsAt: epoch(me.outOfOffice.endsAt),
        external: me.outOfOffice.external,
      },
      automation: {
        autoAwayAfterMinutes: me.autoAwayAfterMinutes,
        dndDuringMeetings: me.dndDuringMeetings,
        dndDuringCalls: me.dndDuringCalls,
      },
    };
  },

  async fetchMany(userIds) {
    return fetchManyPresence(userIds);
  },

  async setManualState(state, resetAfter) {
    await apiPatch('/presence/me', {
      manualState: state === null ? null : TO_WIRE_MANUAL[state],
      resetAfterMinutes: RESET_AFTER_MINUTES[resetAfter],
    });
  },

  async setCustomStatus(status) {
    await apiPatch('/presence/me', {
      status:
        status === null
          ? null
          : {
              emoji: status.emoji,
              text: status.text,
              expiresAt: status.expiresAt === null ? null : new Date(status.expiresAt).toISOString(),
              pinInChat: status.pinInChat,
            },
    });
  },

  /** The socket ping already refreshes the 45s Redis TTL — nothing to send. */
  async heartbeat() {},

  /**
   * SUBSCRIBE FIRST, THEN READ — AND READ AGAIN EVERY TIME THE TAB COMES BACK.
   *
   * A subscription that only ever delivers CHANGES is wrong twice over, and both
   * were reported as one symptom ("my status is stuck and never updates"):
   *
   *   THE RACE. The gateway publishes the user's state at CONNECT time, from
   *   `onConnect`, before this client has sent its `sub` frame — so that frame
   *   lands on nobody. The caller used to `fetchMany` and only then subscribe,
   *   which leaves the same hole at the other end: anything published between
   *   the read and the subscription is lost as well. Whatever the cold read got
   *   wrong then stood until the NEXT change, which for an idle user is the
   *   auto-away flip up to five minutes later. Subscribing first and reading
   *   second closes it in the only direction that cannot lose: the read is
   *   authoritative and strictly later than the subscription, so a frame
   *   published before it is superseded and one published after it is delivered.
   *
   *   THE WAKE. `createRealtimeClient`'s own `wake()` returns immediately when
   *   the socket is still open — by design, it exists to reconnect a dead one.
   *   So coming back to a tab, or restoring the desktop window from the tray,
   *   re-read nothing at all and the user kept staring at whatever was on screen
   *   when they left. A wake is not a reconnect; it is a reason to re-read.
   *
   * Both are the same one-line answer: the current truth is fetched after
   * subscribing, after every transport reconnect, and on every wake. `fetchMany`
   * is idempotent, so doing it more often than strictly necessary costs one
   * small POST and can never make the roster wrong.
   */
  subscribe(userIds, onDelta) {
    const realtime = getRealtimeClient();
    const ids = [...userIds];
    const topics = ids.map((id) => `presence:${id}`);

    let stopped = false;
    let inFlight = false;
    let pending = false;
    /* Not 0: "no refresh has happened yet" is not "one happened at the epoch",
       and the first read must never be delayed by the coalescing window. */
    let lastRefreshAt = Number.NEGATIVE_INFINITY;
    let queued: ReturnType<typeof setTimeout> | null = null;

    /** Reads, or remembers to read the moment the read in flight comes back. */
    const run = (): void => {
      if (stopped || ids.length === 0) return;
      if (inFlight) {
        pending = true;
        return;
      }
      lastRefreshAt = Date.now();
      inFlight = true;
      void fetchManyPresence(ids)
        .then((rows) => {
          if (!stopped) onDelta(rows);
        })
        .catch(() => undefined)
        .finally(() => {
          inFlight = false;
          if (pending && !stopped) {
            pending = false;
            run();
          }
        });
    };

    /** Coalesces a burst — alt-tabbing fires `focus` and `visibilitychange` in
        the same breath — without dropping it: the LAST wake in a burst is the
        one whose answer the user is about to look at. */
    const refresh = (): void => {
      if (stopped || ids.length === 0) return;
      const wait = REFRESH_MIN_GAP_MS - (Date.now() - lastRefreshAt);
      if (wait <= 0 && !inFlight) {
        run();
        return;
      }
      if (queued !== null) return;
      queued = setTimeout(() => {
        queued = null;
        run();
      }, Math.max(wait, 0));
    };

    const off = realtime.onEvent(({ event }) => {
      if (event.type === 'presence.update') {
        onDelta([fromSummary(event.userId, event.presence)]);
        return;
      }
      if (event.type === 'presence.bulk') {
        onDelta(event.updates.map((row) => fromSummary(row.userId, row.presence)));
      }
    });

    /* Registered BEFORE the socket is asked to connect, so the transition into
       `online` cannot happen between the two and go unheard. */
    const offState = realtime.onState((state) => {
      if (state === 'online') refresh();
    });

    realtime.connect();
    realtime.subscribe(topics);
    refresh();

    /*
     * A HUMAN LOOKING AT THIS AGAIN. `focus` is the one a desktop window
     * restored from the tray fires — the shell shows the BrowserWindow and the
     * renderer is focused — so the tray path needs nothing of its own here.
     */
    const wake = (): void => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      refresh();
    };
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') refresh();
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', wake);
      window.addEventListener('pageshow', wake);
      window.addEventListener('online', wake);
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisible);
    }

    return () => {
      stopped = true;
      if (queued !== null) clearTimeout(queued);
      queued = null;
      off();
      offState();
      if (typeof window !== 'undefined') {
        window.removeEventListener('focus', wake);
        window.removeEventListener('pageshow', wake);
        window.removeEventListener('online', wake);
      }
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisible);
      }
      realtime.unsubscribe(topics);
    };
  },
};
